/**
 * ECDH-based encryption/decryption for P-256 (secp256r1) curve.
 * This replaces eciesjs which only supports secp256k1.
 */
import { EcdsaSecp256r1 } from '@streamr/utils';
/**
 * Encrypts data for a recipient using ECDH + HKDF + AES-GCM
 *
 * @param data - Data to encrypt
 * @param recipientPublicKey - Recipient's P-256 public key (raw format, 65 bytes uncompressed)
 * @returns Encrypted package: ephemeralPublicKey (65 bytes) + iv (12 bytes) + ciphertext
 */
export async function encrypt(data, recipientPublicKey) {
    // WebCrypto importKey('raw') requires uncompressed P-256 keys (65 bytes, 0x04 prefix).
    // UserIDs may be compressed (33 bytes, 0x02/0x03 prefix); Chrome tolerates this but Safari doesn't.
    const signingUtil = new EcdsaSecp256r1();
    const uncompressedKey = signingUtil.getUncompressedPublicKey(recipientPublicKey);
    // 1. Generate ephemeral key pair
    const ephemeralKeyPair = await crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits', 'deriveKey']);
    // 2. Import recipient's public key
    const recipientCryptoKey = await crypto.subtle.importKey('raw', uncompressedKey.buffer.slice(uncompressedKey.byteOffset, uncompressedKey.byteOffset + uncompressedKey.byteLength), { name: 'ECDH', namedCurve: 'P-256' }, false, []);
    // 3. Derive shared secret using ECDH
    const sharedSecret = await crypto.subtle.deriveBits({ name: 'ECDH', public: recipientCryptoKey }, ephemeralKeyPair.privateKey, 256);
    // 4. Derive AES key from shared secret using HKDF
    const hkdfKey = await crypto.subtle.importKey('raw', sharedSecret, 'HKDF', false, ['deriveKey']);
    const aesKey = await crypto.subtle.deriveKey({
        name: 'HKDF',
        hash: 'SHA-256',
        salt: new Uint8Array(), // No salt for simplicity
        info: new Uint8Array(), // No info for simplicity
    }, hkdfKey, { name: 'AES-GCM', length: 256 }, false, ['encrypt']);
    // 5. Generate random IV
    const iv = crypto.getRandomValues(new Uint8Array(12));
    // 6. Encrypt data with AES-GCM
    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength));
    // 7. Export ephemeral public key
    const ephemeralPublicRaw = await crypto.subtle.exportKey('raw', ephemeralKeyPair.publicKey);
    // 8. Package: ephemeralPublicKey (65) + iv (12) + ciphertext
    const result = new Uint8Array(ephemeralPublicRaw.byteLength + iv.length + ciphertext.byteLength);
    result.set(new Uint8Array(ephemeralPublicRaw), 0);
    result.set(iv, ephemeralPublicRaw.byteLength);
    result.set(new Uint8Array(ciphertext), ephemeralPublicRaw.byteLength + iv.length);
    return result;
}
/**
 * Decrypts data encrypted with the encrypt function
 *
 * @param recipientPrivateKey - Recipient's P-256 private key (raw format, 32 bytes)
 * @param encryptedPackage - Encrypted package from encrypt()
 * @returns Decrypted data
 */
export async function decrypt(recipientPrivateKey, encryptedPackage) {
    if (encryptedPackage.length < 77) {
        throw new Error(`Invalid encrypted package: expected at least 77 bytes, got ${encryptedPackage.length}. ` +
            'This might be data encrypted with eciesjs (secp256k1) instead of P-256 ECDH.');
    }
    const ephemeralPublicRaw = encryptedPackage.slice(0, 65);
    const iv = encryptedPackage.slice(65, 77);
    const ciphertext = encryptedPackage.slice(77);
    const jwk = privateKeyToEcdhJwk(recipientPrivateKey);
    let recipientPrivateCryptoKey;
    try {
        recipientPrivateCryptoKey = await crypto.subtle.importKey('jwk', jwk, { name: 'ECDH', namedCurve: 'P-256' }, false, ['deriveBits', 'deriveKey']);
    }
    catch (error) {
        console.error('Failed to import recipient private key:', error);
        throw error;
    }
    const ephemeralPublicCryptoKey = await crypto.subtle.importKey('raw', ephemeralPublicRaw.buffer.slice(ephemeralPublicRaw.byteOffset, ephemeralPublicRaw.byteOffset + ephemeralPublicRaw.byteLength), { name: 'ECDH', namedCurve: 'P-256' }, false, []);
    const sharedSecret = await crypto.subtle.deriveBits({ name: 'ECDH', public: ephemeralPublicCryptoKey }, recipientPrivateCryptoKey, 256);
    const hkdfKey = await crypto.subtle.importKey('raw', sharedSecret, 'HKDF', false, ['deriveKey']);
    const aesKey = await crypto.subtle.deriveKey({
        name: 'HKDF',
        hash: 'SHA-256',
        salt: new Uint8Array(),
        info: new Uint8Array(),
    }, hkdfKey, { name: 'AES-GCM', length: 256 }, false, ['decrypt']);
    try {
        const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, aesKey, ciphertext.buffer.slice(ciphertext.byteOffset, ciphertext.byteOffset + ciphertext.byteLength));
        return new Uint8Array(plaintext);
    }
    catch (error) {
        console.error('AES-GCM decryption failed:', error);
        throw new Error('Failed to decrypt message. This could be because: ' +
            '1) The message was encrypted with a different key/system (e.g., eciesjs with secp256k1), ' +
            '2) The message was corrupted, or ' +
            '3) The wrong private key was used.');
    }
}
function uint8ArrayToBase64Url(bytes) {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}
/**
 * Converts a raw P-256 private key to JWK format for WebCrypto importKey.
 * Uses JWK instead of PKCS#8 because Safari/WebKit rejects minimal PKCS#8
 * EC keys that lack the optional public key field.
 */
function privateKeyToEcdhJwk(rawPrivateKey) {
    const signingUtil = new EcdsaSecp256r1();
    const publicKey = signingUtil.getPublicKeyFromPrivateKey(rawPrivateKey, false);
    const x = publicKey.subarray(1, 33);
    const y = publicKey.subarray(33);
    return {
        kty: 'EC',
        crv: 'P-256',
        x: uint8ArrayToBase64Url(x),
        y: uint8ArrayToBase64Url(y),
        d: uint8ArrayToBase64Url(rawPrivateKey),
        key_ops: ['deriveBits', 'deriveKey'],
        ext: true,
    };
}
